import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectForSelectFragment,
  ProjectSelectorDocument,
  ProjectSelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import i18n from 'modules/core/lib/i18n'

import { SubprojectSelector } from 'modules/project_management/components/selectors/SubprojectSelector'

import {
  ProjectSelectorMultipleProps,
  ProjectSelectorSingleWithSubprojectsProps,
  ProjectSelectorSingleWithoutSubprojectsProps,
} from './types'

const ProjectItem = ({ project }: { project: ProjectForSelectFragment }) => {
  return (
    <Box gap="s2">
      <Box flexDirection="row" alignItems="center" gap="s8">
        <Text.Normal weight="medium" color="grey1000">
          {project.name}
        </Text.Normal>
      </Box>
      {project.code && (
        <Box flexDirection="row">
          <Text.Normal size="200">{project.code}</Text.Normal>
        </Box>
      )}
    </Box>
  )
}

export const ProjectSelector: React.FC<
  | ProjectSelectorSingleWithSubprojectsProps
  | ProjectSelectorSingleWithoutSubprojectsProps
  | ProjectSelectorMultipleProps
> = (props) => {
  const {
    value,
    mandatory,
    multiple,
    notClosed,
    assignedEmployeeIds,
    withoutClient = false,
    skipViewerByEmployeeId,
  } = props

  const extractConnection = (data: ProjectSelectorQuery) => {
    return data.projectManagement.projectsConnection
  }

  const renderEntity = (entity: ProjectForSelectFragment) => {
    return <ProjectItem project={entity} />
  }

  const variables = {
    assignedEmployeeIds: assignedEmployeeIds,
    notClosed: notClosed,
    withoutClient,
    skipViewerByEmployeeId,
  }

  const commonProps = {
    document: ProjectSelectorDocument,
    extractConnection,
    label: i18n.t('project_management.modals.add_project.select_project'),
    renderEntity,
    variables,
    mandatory,
    notClosed,
  }

  if (multiple) {
    const selectorProps = { ...commonProps, ...props }

    return (
      <EntitySelector.Multiple
        {...selectorProps}
        renderValue={(projects) => {
          return (
            <Text.Normal ellipsis>
              {projects.length > 0
                ? projects.map((project) => project.name).join(', ')
                : i18n.t('common.select_multiple')}
            </Text.Normal>
          )
        }}
      />
    )
  }

  const selectorProps = { ...commonProps, ...props }
  const withSubprojects =
    !multiple && props.subprojectValue !== undefined && !!props.onChangeSubproject

  return (
    <Box gap="s24">
      <EntitySelector
        {...selectorProps}
        renderValue={(entity: ProjectForSelectFragment | null) =>
          entity !== null ? (
            <Text.Normal ellipsis>{entity.name}</Text.Normal>
          ) : (
            <Text.Normal ellipsis>{i18n.t('common.select')}</Text.Normal>
          )
        }
      />
      {withSubprojects && value && (value?.subprojectsConnection.totalCount || 0 > 0) && (
        <SubprojectSelector
          projectId={value.id}
          value={props.subprojectValue}
          onChange={props.onChangeSubproject}
          disabled={props.disabled}
          onlyActive={props.subprojectOnlyActive}
          notClosed={props.subprojectNotClosed}
        />
      )}
    </Box>
  )
}
